import React, { useCallback, useEffect, useState } from 'react';
import { schoolTestimonials, testimonials } from '../data/mock';
import { Instagram } from 'lucide-react';
import { useAudience } from '../contexts/AudienceContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useAutoScrollCarousel } from '../hooks/useAutoScrollCarousel';

/**
 * Fixed card widths. Below `md`, avoid `100vw` in widths — on iOS/Android it is often wider than the
 * real layout box and causes body-level horizontal “infinite” scroll after deploy.
 */
const CARD_W =
  'box-border w-[16.25rem] min-w-[16.25rem] max-w-[16.25rem] shrink-0 grow-0 sm:w-[17rem] sm:min-w-[17rem] sm:max-w-[17rem] md:w-[min(19.5rem,calc(100vw_-_3rem))] md:min-w-[min(19.5rem,calc(100vw_-_3rem))] md:max-w-[min(19.5rem,calc(100vw_-_3rem))] lg:w-[min(21rem,calc(100vw_-_3.25rem))] lg:min-w-[min(21rem,calc(100vw_-_3.25rem))] lg:max-w-[min(21rem,calc(100vw_-_3.25rem))]';

const STAR_PATH =
  'M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z';

function StarRow({ rating }) {
  const n = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  return (
    <div className="flex shrink-0 items-center justify-center gap-0.5 pt-1" aria-label={`${n} out of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`h-4 w-4 ${
            i < n ? 'text-amber-400 fill-current' : 'fill-slate-100 text-slate-300 stroke stroke-[1.1]'
          }`}
          viewBox="0 0 20 20"
          aria-hidden
        >
          <path d={STAR_PATH} />
        </svg>
      ))}
    </div>
  );
}

function ReviewCard({ testimonial, instanceKey, variant = 'grid' }) {
  const isCarousel = variant === 'carousel';
  const [showFull, setShowFull] = useState(false);
  const subtitle = [testimonial.role ?? testimonial.class, testimonial.location].filter(Boolean).join(' · ');
  const socialUrl = testimonial.socialUrl || '';
  const avatarUrl = testimonial.avatarUrl || '';
  const text = String(testimonial.text || '');

  const closeFull = useCallback(() => setShowFull(false), []);

  useEffect(() => {
    if (!showFull) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeFull();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showFull, closeFull]);

  return (
    <article
      className={`student-review-card group relative flex h-[396px] min-h-[396px] max-h-[396px] shrink-0 flex-col self-start overflow-hidden overflow-x-hidden overflow-y-hidden rounded-2xl border border-slate-200/90 bg-white/95 p-4 shadow-[0_16px_40px_-20px_rgba(15,23,42,0.32)] ring-1 ring-slate-900/[0.04] transition duration-300 hover:border-teal-200/90 hover:shadow-[0_22px_48px_-24px_rgba(13,148,136,0.26)] sm:p-5 md:h-[352px] md:min-h-[352px] md:max-h-[352px] ${
        isCarousel ? `student-review-card--carousel ${CARD_W}` : 'w-full max-w-md mx-auto'
      }`}
    >
      <div className="mb-2 flex shrink-0 items-start justify-between gap-2">
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:px-3 sm:py-1 sm:text-[10px] sm:tracking-[0.2em]">
          {testimonial.class ? 'Student note' : 'School voice'}
        </span>
        {socialUrl ? (
          <a
            href={socialUrl}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={(e) => e.stopPropagation()}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200/90 bg-gradient-to-br from-white to-slate-50 text-[#E4405F] shadow-sm transition hover:scale-105 hover:border-pink-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/80"
            aria-label="Open ChooseRightCareer on Instagram"
          >
            <Instagram className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          </a>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-center text-center">
        <div className="relative mb-2">
          <div
            className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-teal-400/35 via-blue-400/25 to-violet-400/35 opacity-80 blur-md transition md:group-hover:opacity-100"
            aria-hidden
          />
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              width={64}
              height={64}
              loading="lazy"
              decoding="async"
              className="relative h-14 w-14 max-h-14 max-w-14 rounded-full object-cover ring-[3px] ring-white shadow-md sm:h-16 sm:w-16 sm:max-h-16 sm:max-w-16"
            />
          ) : (
            <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-slate-200 to-slate-100 text-lg font-bold text-slate-500 ring-[3px] ring-white sm:h-16 sm:w-16 sm:text-xl">
              {String(testimonial.name || '?').charAt(0)}
            </div>
          )}
        </div>
      </div>

      <div className="relative mt-0.5 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="relative max-h-[5.5rem] min-h-0 shrink-0 overflow-hidden px-0.5 md:max-h-[4.25rem]">
          <p className="line-clamp-3 break-words text-center text-[14px] leading-snug text-slate-700 sm:text-[15px] sm:leading-relaxed md:line-clamp-2">
            <span className="text-slate-400">&ldquo;</span>
            {text}
            <span className="text-slate-400">&rdquo;</span>
          </p>
        </div>

        {!showFull ? (
          <button
            type="button"
            className="relative z-[1] mx-auto mt-1.5 shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold text-teal-800 underline decoration-teal-300 decoration-2 underline-offset-2 transition hover:text-teal-950 md:hidden sm:text-[11px]"
            aria-expanded={false}
            aria-controls={`review-full-${instanceKey}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setShowFull(true);
            }}
          >
            Read full review
          </button>
        ) : null}

        <div className="mt-auto flex shrink-0 flex-col items-center pt-1">
          <StarRow rating={testimonial.rating} />
        </div>
      </div>

      <footer className="mt-2 shrink-0 border-t border-slate-100 pt-2 text-center">
        <p className="font-display text-[15px] font-semibold tracking-tight text-slate-900 sm:text-base">{testimonial.name}</p>
        <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">{subtitle}</p>
      </footer>

      {/* Full review: whole card so mobile can scroll vertically; horizontal stay on carousel only */}
      <div
        className={`absolute inset-0 z-[45] flex max-h-full min-h-0 flex-col overflow-hidden rounded-2xl transition-opacity duration-200 ${
          showFull
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0 md:group-hover:pointer-events-auto md:group-hover:opacity-100'
        }`}
        id={`review-full-${instanceKey}`}
        role="region"
        aria-label="Full review text"
      >
        <div className="touch-pan-y flex h-full min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain border border-teal-100/90 bg-white/[0.99] p-4 text-left text-[14px] leading-relaxed text-slate-800 shadow-lg ring-1 ring-slate-900/5 sm:p-4 sm:text-[15px]">
          <p className="min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
            <span className="text-slate-400">&ldquo;</span>
            {text}
            <span className="text-slate-400">&rdquo;</span>
          </p>
          <button
            type="button"
            className="mt-4 w-full shrink-0 rounded-xl bg-slate-900 py-2.5 text-xs font-semibold text-white md:hidden"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              closeFull();
            }}
          >
            Close
          </button>
        </div>
      </div>
    </article>
  );
}

export default function TestimonialsSection() {
  const { isSchool } = useAudience();
  const items = isSchool ? schoolTestimonials : testimonials;
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const carouselRef = useAutoScrollCarousel({ direction: 1, speed: 0.62, resumeAfterMs: 3200 });

  return (
    <section
      className="relative isolate w-full min-w-0 max-w-full overflow-x-clip py-24 sm:py-28"
      aria-labelledby="student-reviews-heading"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(45,212,191,0.12),transparent_55%),radial-gradient(ellipse_90%_60%_at_100%_50%,rgba(59,130,246,0.08),transparent_50%),linear-gradient(180deg,#f8fafc_0%,#ffffff_45%,#f1f5f9_100%)]" />

      <div className="container relative z-10 mx-auto min-w-0 max-w-full px-6">
        <div className="mx-auto mb-8 max-w-xl text-center sm:mb-10">
          <h2
            id="student-reviews-heading"
            className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
          >
            See what students say about ChooseRightCareer.
          </h2>
        </div>

        {reduceMotion ? (
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <ReviewCard key={t.id} testimonial={t} instanceKey={`static-${t.id}`} />
            ))}
          </div>
        ) : (
          <div className="student-reviews-marquee relative mx-auto min-w-0 max-w-full">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[#f8fafc] to-transparent sm:w-16" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[#f1f5f9] to-transparent sm:w-16" />

            <div
              ref={carouselRef}
              className="student-reviews-viewport mx-auto w-full min-w-0 max-w-full md:max-w-[min(100%,55rem)]"
              tabIndex={0}
              aria-label="Student reviews. Scroll horizontally, or leave it to drift slowly from right to left."
            >
              <div className="student-reviews-row">
                {items.map((t) => (
                  <ReviewCard key={t.id} testimonial={t} instanceKey={String(t.id)} variant="carousel" />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
